import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface FiscalCalendar {
  id: string;
  name: string;
  code: string;
  type: 'CALENDAR' | 'FISCAL_445' | 'FISCAL_454' | 'FISCAL_544' | 'WEEKLY' | 'CUSTOM';
  yearStartMonth: number;
  yearStartDay?: number;
  weekStartDay?: number;
  isActive: boolean;
  description?: string;
  periods?: FiscalPeriod[];
}

export interface FiscalPeriod {
  id: string;
  calendarId: string;
  calendar?: FiscalCalendar;
  fiscalYear: number;
  fiscalQuarter: number;
  fiscalMonth: number;
  fiscalWeek?: number;
  periodName: string;
  startDate: string;
  endDate: string;
  workingDays?: number;
  isOpen: boolean;
  isLocked: boolean;
  isClosed: boolean;
  lockedAt?: string;
  lockedById?: string;
  isCurrent?: boolean;
}

// ============================================================================
// Fiscal Calendar Service
// ============================================================================

export const fiscalCalendarService = {
  // Calendars
  async getCalendars(params?: { isActive?: boolean; type?: string }) {
    const response = await apiClient.get('/manufacturing/fiscal-calendars', { params });
    return response.data;
  },

  async getCalendar(calendarId: string) {
    const response = await apiClient.get(`/manufacturing/fiscal-calendars/${calendarId}`);
    return response.data;
  },

  async getActiveCalendar() {
    const response = await apiClient.get('/manufacturing/fiscal-calendars/active');
    return response.data;
  },

  async createCalendar(dto: {
    name: string;
    code: string;
    type: string;
    yearStartMonth: number;
    yearStartDay?: number;
    weekStartDay?: number;
    description?: string;
  }) {
    const response = await apiClient.post('/manufacturing/fiscal-calendars', dto);
    return response.data;
  },

  async updateCalendar(calendarId: string, dto: Partial<FiscalCalendar>) {
    const response = await apiClient.put(`/manufacturing/fiscal-calendars/${calendarId}`, dto);
    return response.data;
  },

  async setActiveCalendar(calendarId: string) {
    const response = await apiClient.put(`/manufacturing/fiscal-calendars/${calendarId}/activate`);
    return response.data;
  },

  async deleteCalendar(calendarId: string) {
    await apiClient.delete(`/manufacturing/fiscal-calendars/${calendarId}`);
  },

  // Periods
  async getPeriods(
    calendarId: string,
    params?: {
      fiscalYear?: number;
      fiscalQuarter?: number;
      fiscalMonth?: number;
      isOpen?: boolean;
      startDateFrom?: string;
      startDateTo?: string;
    },
  ) {
    const response = await apiClient.get(`/manufacturing/fiscal-calendars/${calendarId}/periods`, { params });
    return response.data;
  },

  async createPeriod(
    calendarId: string,
    dto: {
      fiscalYear: number;
      fiscalQuarter: number;
      fiscalMonth: number;
      fiscalWeek?: number;
      periodName: string;
      startDate: string;
      endDate: string;
      workingDays?: number;
      isOpen?: boolean;
    },
  ) {
    const response = await apiClient.post(`/manufacturing/fiscal-calendars/${calendarId}/periods`, dto);
    return response.data;
  },

  async togglePeriodStatus(periodId: string) {
    const response = await apiClient.put(`/manufacturing/fiscal-periods/${periodId}/toggle-status`);
    return response.data;
  },

  async deletePeriod(periodId: string) {
    await apiClient.delete(`/manufacturing/fiscal-periods/${periodId}`);
  },

  // Bulk Period Generation
  async generatePeriodsForYear(
    calendarId: string,
    dto: {
      fiscalYear: number;
      overwriteExisting?: boolean;
      calculateWorkingDays?: boolean;
      holidays?: string[];
    },
  ) {
    const response = await apiClient.post(`/manufacturing/fiscal-calendars/${calendarId}/generate-periods`, dto);
    return response.data;
  },

  // Calendar Types
  async getCalendarTypes() {
    return [
      { value: 'CALENDAR', label: 'Calendar Year', description: 'Standard January-December calendar' },
      { value: 'FISCAL_445', label: '4-4-5 Fiscal', description: '4-4-5 week pattern per quarter' },
      { value: 'FISCAL_454', label: '4-5-4 Fiscal', description: '4-5-4 week pattern per quarter' },
      { value: 'FISCAL_544', label: '5-4-4 Fiscal', description: '5-4-4 week pattern per quarter' },
      { value: 'WEEKLY', label: 'Weekly', description: 'Week-based periods' },
      { value: 'CUSTOM', label: 'Custom', description: 'Manually defined periods' },
    ];
  },

  // ──────────────────────────────────────────────────────────────────
  // Period Locking (Accounting)
  // ──────────────────────────────────────────────────────────────────
};

export default fiscalCalendarService;
